<template>
  <div class="visitor-parent-search" ref="dynamicSelect">
    <div class="input-container" @click="toggleOptions">
      <input
        :id="searchInputId"
        ref="searchInputRef"
        class="input"
        :class="{ open: showOptions }"
        type="text"
        role="combobox"
        :aria-expanded="showOptions ? 'true' : 'false'"
        :aria-controls="listboxId"
        aria-haspopup="listbox"
        aria-autocomplete="list"
        :placeholder="placeholder"
        :value="inputValue"
        :aria-label="placeholder"
        @input="onInput"
        @keydown="onInputKeydown"
      />
      <span class="chevron" :class="{ open: showOptions }" aria-hidden="true">
        <i class="ri-arrow-down-s-line"></i>
      </span>
    </div>
    <ul
      v-if="showOptions"
      :id="listboxId"
      ref="listboxRef"
      role="listbox"
      :aria-label="placeholder"
      class="options ps__child--consume"
    >
      <template v-if="options.length">
        <li
          v-for="(option, index) in options"
          :key="`${option.label}-${index}`"
          class="visitor-parent-search__li"
          role="presentation"
        >
          <button
            type="button"
            role="option"
            class="visitor-parent-search__option"
            :aria-selected="
              isSameOptionValue(option.value, modelValue) ? 'true' : 'false'
            "
            @click="selectOption(option)"
          >
            <slot v-if="hasOptionSlot" name="option" :value="option"></slot>
            <template v-else>
              {{ option.label }}
            </template>
          </button>
        </li>
      </template>
      <template v-else>
        <li class="no-results">Sorry, no matching options</li>
      </template>
    </ul>
    <span
      v-if="showOptions && options.length === 0 && inputValue.trim().length > 0"
      class="visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      :key="`${inputValue}-${options.length}`"
    >
      Sorry, no matching options
    </span>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from "vue"

export default {
  name: "VisitorParentSearch",
  props: {
    modelValue: {
      type: Object,
      default: null
    },
    options: {
      type: Array,
      default: () => [],
      validator: (options) => {
        return options.every((option) => {
          return option.label && option.value
        })
      }
    },
    placeholder: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue", "search"],
  setup(props, { emit, slots }) {
    const uid = Math.random().toString(36).slice(2, 11)
    const searchInputId = `visitor-parent-search-in-${uid}`
    const listboxId = `visitor-parent-search-lb-${uid}`

    const inputValue = ref("")
    const showOptions = ref(false)
    const dynamicSelect = ref(null)
    const searchInputRef = ref(null)
    const listboxRef = ref(null)

    const hasOptionSlot = computed(() => !!slots.option)

    const isSameOptionValue = (a, b) => {
      if (a == null || b == null) return false
      if (a === b) return true
      const idA = a?.id ?? a?.parent_id
      const idB = b?.id ?? b?.parent_id
      if (idA != null && idB != null) return String(idA) === String(idB)
      return false
    }

    const onInput = (event) => {
      inputValue.value = event.target.value
      showOptions.value = true
      emit("search", inputValue.value)
    }

    const selectOption = (option) => {
      inputValue.value = option.label
      emit("update:modelValue", option.value)
      emit("search", inputValue.value)
      showOptions.value = false
    }

    const toggleOptions = () => {
      showOptions.value = !showOptions.value
    }

    const focusFirstOption = () => {
      nextTick(() => {
        const root = listboxRef.value
        const btn = root?.querySelector?.(".visitor-parent-search__option")
        btn?.focus?.()
      })
    }

    const onInputKeydown = (event) => {
      if (event.key === "Escape") {
        showOptions.value = false
        return
      }
      if (
        event.key === "ArrowDown" &&
        showOptions.value &&
        props.options.length > 0
      ) {
        event.preventDefault()
        focusFirstOption()
      }
    }

    const handleClickOutside = (event) => {
      if (dynamicSelect.value && !dynamicSelect.value.contains(event.target)) {
        showOptions.value = false
      }
    }

    onMounted(() => {
      document.addEventListener("click", handleClickOutside)
    })

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside)
    })

    return {
      hasOptionSlot,
      inputValue,
      showOptions,
      onInput,
      selectOption,
      toggleOptions,
      dynamicSelect,
      searchInputId,
      listboxId,
      searchInputRef,
      listboxRef,
      onInputKeydown,
      isSameOptionValue
    }
  }
}
</script>

<style scoped>
.visitor-parent-search__li {
  list-style: none;
  margin: 0;
  padding: 0;
}

.visitor-parent-search__option {
  display: block;
  width: 100%;
  margin: 0;
  padding: inherit;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
}

.visitor-parent-search__option:focus-visible {
  outline: 2px solid var(--v3-focus-outline, #2b6cb0);
  outline-offset: 1px;
}
</style>
